/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Grid, Stack, Box, Typography } from "@mui/material";
import FormField from "components/generals/FormInput";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import { patientFormYUPValidation } from "utils/form.utils";
import {
  HeaderEnum,
  PatientInsuranceInterface,
  PatientInterface,
  PatientPharmacyInterface,
  PharmacyInterface,
  ProviderInterface,
  QueryResultInterface,
  AssetInterface,
  PatientAssetRelationInterface,
} from "@malmur-technologies/medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { FormProps } from "interfaces";
import StepperFormActionButtons from "../custom/StepperFormActionButtons";
import { H4 } from "components/generals/_Titles";
import { AutoCompleteOption, FormActionType } from "types";
import { CoreAPI_URLs } from "constants/http.const";
import { Method } from "axios";
import FormInputPassword from "components/generals/FormInputPassword";
import FormSwitch from "components/generals/Switch";
import { useTranslation } from "react-i18next";
import BadgeIcon from "@mui/icons-material/Badge";
import Autocomplete from "components/generals/Autocomplete";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";
import TabsComponent from "components/layout/PatientTabPanel";
import { DocumentFolder } from "interfaces";
import { checkFormValidity } from "utils/form.utils";
import { CountryEnum } from "@malmur-technologies/medicalsystem-shared";

/**
 * Props for the PatientForm component.
 * @interface PatientFormProps
 * @extends {FormProps<PatientInterface>}
 * @property {Dispatch<SetStateAction<PatientPharmacyInterface[]>>} [setPharmacyRelationList] - State setter for patient pharmacy relationships
 * @property {Dispatch<SetStateAction<Partial<PharmacyInterface>>>} [setDefaultPharmacy] - State setter for default pharmacy
 * @property {Dispatch<SetStateAction<PatientInsuranceInterface[]>>} [setInsuranceRelationList] - State setter for patient insurance relationships
 */
interface PatientFormProps extends FormProps<PatientInterface> {
  setPharmacyRelationList?: Dispatch<SetStateAction<PatientPharmacyInterface[]>>;
  setDefaultPharmacy?: Dispatch<SetStateAction<Partial<PharmacyInterface>>>;
  setInsuranceRelationList?: Dispatch<SetStateAction<PatientInsuranceInterface[]>>;
}

/**
 * Form component for patient-specific information and medical data.
 * Handles account number, SSN, PCP assignment, consent forms, pharmacy, and insurance relationships.
 * Can be used standalone or as part of a stepper workflow.
 * 
 * Features:
 * - Account number and SSN management
 * - Primary Care Provider (PCP) selection with autocomplete
 * - Medical history consent checkbox
 * - Release of information consent
 * - Pharmacy relationships management via tabs
 * - Insurance relationships management via tabs
 * - Document management and file uploads
 * - Password field for new patient accounts
 * - Form validation with Yup schema
 * - Integration with stepper for multi-step forms
 * - API integration for create/update operations
 * - Data refresh on model changes
 * 
 * Tabs:
 * - Pharmacy: Manage patient's preferred pharmacies
 * - Insurance: Manage patient's insurance plans
 * - Documents: Upload and manage patient documents
 * 
 * Use Cases:
 * - Creating new patient accounts
 * - Updating existing patient information
 * - Part of multi-step patient registration
 * - Managing patient-provider relationships
 * 
 * @param {PatientFormProps} props - Component props
 * @returns {React.ReactElement} The patient information form with tabs
 */
const PatientForm: React.FC<PatientFormProps> = ({
  model,
  action,
  closeAction,
  nextStep,
  backStepHandler,
  lastStep,
  firstStep,
  setPharmacyRelationList,
  setDefaultPharmacy,
  setInsuranceRelationList,
  ...props
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [isDataUpdated, setIsDataUpdated] = useState(false);
  const generalSettings = useSelector(selectGeneralSettings);

  useEffect(() => {
    if (model.id) {
      sendRequest(CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS, null, 'GET', undefined, [
        {
          header: HeaderEnum.PATIENT_ID,
          value: model.id,
        },
      ]);
    }
  }, [model.id]);

  useEffect(() => {
    if (isDataUpdated) {
      setIsDataUpdated(false);
    }
  }, [isDataUpdated]);

  useEffect(() => {
    sendRequest(CoreAPI_URLs.DOCTOR_GET_ALL, onGetDoctorsHandler, "GET");
  }, []);

  const { t } = useTranslation();
  const [doctorList, setDoctorList] = useState<ProviderInterface[]>([]);
  const [boolNextButton, setBoolNextButton] = useState<boolean>(true);

  // When insurance module is disabled, allow to proceed
  useEffect(() => {
    if (!generalSettings?.isInsuranceModuleEnabled) {
      setBoolNextButton(false);
    }
  }, [generalSettings?.isInsuranceModuleEnabled]);

  const submitHandler = (model: Partial<PatientInterface>) => {
    if (!boolNextButton) {
      if (model.ssn == "") {
        model.ssn = undefined;
      }
      if (nextStep) {
        nextStep(model);
      } else {
        const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
        const successMessageText = action === FormActionType.NEW ? t("patientForm.patientCreated") : t("patientForm.patientUpdated");
        sendRequest(
          CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS,
          onPatientUpdateHandler,
          method,
          JSON.stringify(model),
          [{ header: HeaderEnum.RESOURCE_ID, value: model.id }],
          { successMessage: successMessageText }
        );
      }
    }
  };
  const onGetDoctorsHandler = (data: QueryResultInterface<ProviderInterface>) => {
    const sortedDoctors = data.results.sort((a, b) => a.person.name.localeCompare(b.person.name));
    setDoctorList(sortedDoctors);
  };

  const onPatientUpdateHandler = (patient: PatientInterface) => {
    if (action === FormActionType.MODIFY) {
      props.setModelList(
        props.modelList.map((model) => (model.id === patient.id ? patient : model))
      );
    } else {
      props.setModelList((oldArray) => [...oldArray, patient]);
    }
  };

  const doctorOptions = useMemo<AutoCompleteOption[]>(() => {
    if (doctorList.length === 0) {
      return [];
    } else {
      return doctorList
        .map((a) => ({
          label: `${a.person.name} ${a.person.lastname}`.toUpperCase(),
          id: a.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  }, [doctorList]);

  return (
    <FormikForm
      onSubmit={(model) => {
        model.accountNumber = undefined;
        submitHandler(model);
      }}
      initialValues={model}
      validationSchema={patientFormYUPValidation(t, generalSettings?.country)}
    >
      {({ values, setFieldValue, errors, touched }: FormikProps<Partial<PatientInterface>>) => (
        <Form id="patientForm" autoComplete="true">
          <Stack alignItems="center" direction="row" gap={2} mb={3}>
            <BadgeIcon fontSize="large" />
            <H4>{t("patientForm.patientData")}</H4>
          </Stack>
          <Grid container spacing={2}>
            {values.accountNumber > 0 ? (
              <Grid item xs={12} md={4}>
                <FormField
                  type="number"
                  label={t("patientForm.accountNumber")}
                  name="accountNumber"
                  readOnly={true}
                  value={values.accountNumber}
                />
              </Grid>
            ) : (
              <Grid item xs={12} md={4}>
                <FormField
                  type="text"
                  label={t("patientForm.accountNumber")}
                  name="accountNumber"
                  readOnly={true}
                  value={t("patientForm.automaticallyGenerated")}
                />
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <FormInputPassword
                label={generalSettings?.country === CountryEnum.MEXICO ? t("patientForm.rfc") : t("patientForm.ssn")}
                name="ssn"
                value={values.ssn || ""}
                showModal={true}
                modalBody={generalSettings?.country === CountryEnum.MEXICO ? t("patientForm.wouldShowRfc") : t("patientForm.wouldShowSsn")}
                modalTitle={generalSettings?.country === CountryEnum.MEXICO ? t("patientForm.showRfc") : t("patientForm.showSsn")}
                forceHidePassword={values.accountNumber > 0}
                inputProps={{
                  maxLength: generalSettings?.country === CountryEnum.MEXICO ? 18 : 9
                }}
              />
            </Grid>
            <Grid item xs={12} id={"cy-referringProvideridFormContainer"}>
              <Autocomplete
                id="cy-referringProviderid"
                options={doctorOptions}
                loading={isLoading}
                name="referringProvider.id"
                label={t("appointmentForm.doctorProvider")}
                onChange={(_e, value) => {
                  if (value) {
                    const selectedDoctor = doctorList.find(doctor => doctor.id === value.id);
                    if (selectedDoctor) {
                      setFieldValue("referringProvider", selectedDoctor);
                    } else {
                      setFieldValue("referringProvider.id", value.id);
                    }
                  } else {
                    setFieldValue("referringProvider", {});
                  }
                }}
                defaultValue={
                  model?.referringProvider?.id
                    ? {
                      id: model.referringProvider.id,
                      label:
                        model.referringProvider.person?.name && model.referringProvider.person?.lastname
                          ? model.referringProvider.person.name + " " + model.referringProvider.person.lastname
                          : `Doctor ID: ${model.referringProvider.id}`,
                    }
                    : null
                }
                noOptionsText={t("patientForm.noOptions")}
              />
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <FormSwitch
                label={t("patientForm.historyConstent")}
                value={values.historyConsent}
                name="historyConsent"
                tooltip={t('patientForm.historyConsentDescription')}
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <FormSwitch
                label={t("patientForm.releaseInformation")}
                value={values.releaseInformation}
                name="releaseInformation"
                tooltip={t('patientForm.releaseInformationDescription')}
                setFieldValue={setFieldValue}
              />
            </Grid>
            <Grid item xs={12}>
              <TabsComponent
                patientId={model?.id ? model.id : null}
                setPharmacyRelationList={setPharmacyRelationList || null}
                setDefaultPharmacy={setDefaultPharmacy || null}
                setInsuranceRelationList={setInsuranceRelationList || null}
                setBoolNextButton={setBoolNextButton || null}
              />
            </Grid>
          </Grid>
          {checkFormValidity(values, errors, touched) && (
            <Box mt={2} mb={2}>
              <Typography
                variant="body2"
                sx={{
                  color: 'red',
                  textAlign: 'center'
                }}
              >
                {checkFormValidity(values, errors, touched)}
              </Typography>
            </Box>
          )}
          <StepperFormActionButtons
            backStepHandler={backStepHandler}
            nextStep={nextStep}
            firstStep={firstStep}
            lastStep={lastStep}
            isLoading={isLoading}
          />
        </Form>
      )}
    </FormikForm>
  );
};

export default PatientForm;